import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SearchBox from '@/apps/legacy/components/portal/SearchBox'
import PaginationControl from '@/apps/legacy/components/portal/PaginationControl'
import {
  CustomButton,
  ActionButton,
} from '@/apps/legacy/components/portal/CustomButton'
import { DrugGroup } from '@/features/internal/drugs/types/soapTemplate'

interface DrugGroupTableProps {
  /**
   * 表示する医薬品グループデータ
   */
  drugGroups: DrugGroup[]

  /**
   * 読み込み中フラグ
   */
  loading?: boolean

  /**
   * 検索クエリ文字列
   */
  searchTerm?: string

  /**
   * 検索クエリ変更時のコールバック
   */
  onSearchChange?: (query: string) => void

  /**
   * 現在のページ番号
   */
  currentPage?: number

  /**
   * 総ページ数
   */
  totalPages?: number

  /**
   * ページ変更時のコールバック
   */
  onPageChange?: (page: number) => void

  /**
   * 詳細表示ボタンクリック時のコールバック
   */
  onViewDetails?: (id: string) => void

  /**
   * 新規グループ追加ボタンクリック時のコールバック
   */
  onAddGroup?: () => void

  /**
   * AI生成ボタンクリック時のコールバック
   */
  onAiGenerate?: () => void
}

/**
 * 医薬品グループをテーブル形式で表示するコンポーネント
 */
const DrugGroupTable: React.FC<DrugGroupTableProps> = ({
  drugGroups,
  loading = false,
  searchTerm = '',
  onSearchChange,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onViewDetails,
  onAddGroup,
  onAiGenerate,
}) => {
  // 検索実行ハンドラー
  const handleSearch = () => {
    if (onSearchChange) {
      onSearchChange(searchTerm)
    }
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* 検索ボックス */}
        {onSearchChange && (
          <Box sx={{ flex: 1 }}>
            <SearchBox
              placeholder="グループ名で検索..."
              value={searchTerm}
              onChange={onSearchChange}
              onSearch={handleSearch}
            />
          </Box>
        )}

        {/* 新規グループ追加ボタン */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onAddGroup && (
            <ActionButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddGroup}
            >
              新規グループ
            </ActionButton>
          )}
          {onAiGenerate && (
            <CustomButton
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={onAiGenerate}
              size="small"
              sx={{
                py: 0.5,
                px: 1.5,
                flexShrink: 0,
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              AIで生成
            </CustomButton>
          )}
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ mb: 2, maxHeight: 500, overflow: 'auto' }}
      >
        <Table stickyHeader aria-label="医薬品グループテーブル">
          <TableHead>
            <TableRow>
              <TableCell>グループ名</TableCell>
              <TableCell>説明</TableCell>
              <TableCell align="center">医薬品数</TableCell>
              <TableCell align="center">テンプレート数</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
                </TableCell>
              </TableRow>
            ) : drugGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography>グループがありません</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'center',
                      mt: 2,
                    }}
                  >
                    {onAddGroup && (
                      <ActionButton
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={onAddGroup}
                      >
                        新規グループを追加
                      </ActionButton>
                    )}
                    {onAiGenerate && (
                      <Button
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={onAiGenerate}
                        size="small"
                        sx={{
                          py: 0.5,
                          px: 1.5,
                          color: 'text.primary',
                          borderColor: 'text.secondary',
                          flexShrink: 0,
                          '&:hover': {
                            borderColor: 'text.primary',
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        AIで生成
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              drugGroups.map((group) => (
                <TableRow key={group.id} hover>
                  <TableCell>{group.name}</TableCell>
                  <TableCell>{group.description}</TableCell>
                  <TableCell align="center">{group.drug_count}</TableCell>
                  <TableCell align="center">
                    {group.template_group_count}
                  </TableCell>
                  <TableCell align="right">
                    {onViewDetails && (
                      <ActionButton
                        variant="contained"
                        size="small"
                        onClick={() => onViewDetails(group.id)}
                      >
                        詳細
                      </ActionButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページネーション（総ページ数が1より大きい場合のみ表示） */}
      {onPageChange && totalPages > 1 && (
        <PaginationControl
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </Box>
  )
}

export default DrugGroupTable
